"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { Select } from "@/components/ui/select";
import { PiTrash, PiPlusBold, PiX, PiUploadSimple } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityHeaderEdit({
  content,
  onContentChange,
  allSections = [],
}: SectionContentProps & { allSections?: any[] }) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const navItems = content.navItems || [
    { label: "Home", sectionId: "hero" },
    { label: "About", sectionId: "about" },
    { label: "Programs", sectionId: "university-programs" },
    { label: "Campus Life", sectionId: "university-campus" },
    { label: "Research", sectionId: "university-research" }
  ];

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-4">
            {/* LOGO MANAGER */}
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
                Logo / Brand
              </label>

              {/* Current Logo Preview */}
              {content.logo && (
                <div className="bg-white p-3 rounded-lg border border-gray-200 mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Current Logo</p>
                    {content.logo.startsWith("/") || content.logo.startsWith("http") || content.logo.startsWith("data:image") ? (
                      <img src={content.logo} alt="Logo Preview" className="max-h-12 w-auto object-contain mt-1" />
                    ) : (
                      <p className="text-sm font-bold text-gray-950 mt-0.5">{content.logo}</p>
                    )}
                  </div>
                  <button
                    onClick={() => updateContent("logo", "INSTACKS")}
                    className="p-1.5 rounded-md bg-red-50 text-red-500 hover:bg-red-100"
                    title="Remove Logo"
                  >
                    <PiX className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Text input / URL display for brand name or logo URL */}
              <Input
                value={
                  content.logo?.startsWith("data:image")
                    ? "[Uploaded Base64 Logo]"
                    : content.logo || ""
                }
                onChange={(e) => updateContent("logo", e.target.value)}
                placeholder="Brand Name (e.g. INSTACKS) or Logo URL"
                className="!rounded-lg mb-2"
              />

              {/* Upload button */}
              <label className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-white border border-dashed border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:border-blue-400 cursor-pointer transition-all">
                <PiUploadSimple className="w-4 h-4 text-gray-500" />
                <span>Upload Logo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("Logo file size must be less than 5MB");
                      return;
                    }
                    const uploadPromise = homepageApi.uploadImage(file);
                    toast.promise(uploadPromise, {
                      loading: "Uploading logo...",
                      success: "Logo uploaded successfully!",
                      error: "Failed to upload logo",
                    });
                    try {
                      const response = await uploadPromise;
                      if (response?.success && response?.data?.url) {
                        updateContent("logo", response.data.url);
                      }
                    } catch (err) {
                      console.error("Logo upload error:", err);
                    }
                  }}
                />
              </label>
            </div>

            {/* NAVIGATION ITEMS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Navigation Items
                </label>
                <button
                  onClick={() => {
                    updateContent("navItems", [...navItems, { label: "New Link", sectionId: "hero" }]);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
                >
                  <PiPlusBold className="w-3 h-3" /> Add
                </button>
              </div>

              <div className="space-y-3">
                {navItems.map((item: any, idx: number) => {
                  const navItem = typeof item === "string"
                    ? { label: item, sectionId: item.toLowerCase() }
                    : item;

                  return (
                    <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 flex gap-2 relative shadow-xs">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Nav Item
                          </label>
                          <Input
                            value={navItem.label || ""}
                            onChange={(e) => {
                              const currentNav = [...navItems];
                              currentNav[idx] = { ...navItem, label: e.target.value };
                              updateContent("navItems", currentNav);
                            }}
                            className="!rounded-md text-xs"
                            placeholder="Home"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Section Link
                          </label>
                          <Select
                            value={navItem.sectionId || "hero"}
                            onChange={(val: any) => {
                              const v = val?.target?.value || val?.value || val;
                              const currentNav = [...navItems];
                              currentNav[idx] = { ...navItem, sectionId: v };
                              updateContent("navItems", currentNav);
                            }}
                            options={[
                              ...(allSections || [])
                                .filter(s => s && s.type !== "header" && s.type !== "footer")
                                .map((sec, i) => ({
                                  label: `${i + 1}. ${sec.title || sec.type}`,
                                  value: sec.type
                                })),
                              { label: "Custom Link", value: "custom" }
                            ]}
                            className="w-full text-xs !rounded-md"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentNav = navItems.filter((_: any, i: number) => i !== idx);
                          updateContent("navItems", currentNav);
                        }}
                        className="self-center p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50"
                        title="Remove Nav Item"
                      >
                        <PiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
